import { sameCompanyFaq } from "@/lib/brand";
import { createFileRoute } from "@tanstack/react-router";
import { hourlyMinimumFaq } from "@/lib/booking-minimums";
import { BEFORE_YOU_BOOK, SAFETY_FACTS, WHY_NOT_CHEAPEST } from "@/lib/safety-facts";
import SeoLandingPage, {
  buildFaqSchema,
  buildLocalBusinessSchema,
} from "@/components/SeoLandingPage";
import heroImage from "@/assets/quinceanera-sprinter-limo-sunset-atlanta.jpg?format=webp&quality=72&w=1400&imagetools";
import midImage from "@/assets/quinceanera-sprinter-limo-door-atlanta.jpg?format=webp&quality=72&w=1000&imagetools";
import galleryA from "@/assets/quinceanera-sprinter-limo-sunset-atlanta.jpg?format=webp&quality=72&w=900&imagetools";
import galleryB from "@/assets/quinceanera-sprinter-limo-door-atlanta.jpg?format=webp&quality=72&w=900&imagetools";
import introImage from "@/assets/sprinter-limo-hotel-arrival-atlanta.jpg?format=webp&quality=72&w=1200&imagetools";
import lounge from "@/assets/sprinter-lounge.jpg?format=webp&quality=72&w=1200&imagetools";
import limoParty from "@/assets/sprinter-limo-party.jpg?format=webp&quality=72&w=900&imagetools";
import entry from "@/assets/sprinter-entry.jpg?format=webp&quality=72&w=900&imagetools";
import greenLounge from "@/assets/sprinter-party.jpg?format=webp&quality=72&w=900&imagetools";
import faqImage from "@/assets/sprinter-limo-2.jpg?format=webp&quality=72&w=900&imagetools";
import promCard from "@/assets/prom-night-chauffeur-group-atlanta.jpg?format=webp&quality=70&w=800&imagetools";
import weddingCard from "@/assets/wedding-bride-veil-celebration-atlanta.jpg?format=webp&quality=70&w=800&imagetools";
import storyCard from "@/assets/maria-priestly-empress-elite.jpg?format=webp&quality=70&w=800&imagetools";

const PAGE_URL = "https://atlantaluxurysprinters.com/quinceanera-limo-atlanta";
const PAGE_TITLE = "Quinceañera Limo Atlanta | Sprinter Limo for Your Quince";
const PAGE_DESC =
  "Chauffeured Mercedes-Benz Sprinter Limousine for Atlanta quinceañeras. Room for the ball gown and her court, up to 14 guests. From $250/hour. Call (678) 431-7222.";

const SAFETY = "Safety & Licensing";
const PRICING = "Pricing & Booking";
const THE_DAY = "On Her Day";

const faqs = [
  {
    group: SAFETY,
    q: "Are you licensed and insured in Georgia?",
    a: "Yes. We are registered with the State of Georgia, operate 100% legally and carry commercial insurance. Every chauffeur is fingerprinted and background-checked, and our vehicles have cameras and AI driver-safety technology.",
  },
  {
    group: SAFETY,
    q: "Can the family follow the ride?",
    a: "Yes. Track your chauffeur live in our app, so you always know when they'll pull up.",
  },
  {
    group: SAFETY,
    q: "Is alcohol allowed on a quinceañera ride?",
    a: "BYOB is only for guests 21+. We don't sell or provide alcohol, and there is none for her, her court or any guest under 21. The Sprinter has a built-in cooler for water and soft drinks.",
  },
  {
    group: PRICING,
    q: "How much does a quinceañera limo cost in Atlanta?",
    a: "Sprinters start from $250 per hour. Every quinceañera gets a custom quote, given upon request, based on the hours, the stops and the vehicle. A 50% deposit secures your date, and your final quote is always in writing before you book.",
  },
  hourlyMinimumFaq({ group: PRICING }),
  sameCompanyFaq({ group: PRICING }),
  {
    group: PRICING,
    q: "Why do some quinceañera limo quotes cost so much less?",
    a: "Doing this legally costs real money: state certification, commercial insurance, fingerprinting and background checks, and safety technology in the vehicles. Some companies quote a low price because they skip those steps and operate illegally. We don't cut corners, and our price reflects what it actually costs.",
  },
  {
    group: PRICING,
    q: "How many people fit for a quinceañera?",
    a: "The Sprinter Limousine (Limo Style) seats up to 14, so the quinceañera and her court of damas and chambelanes can ride together. A full ball gown takes extra room, so if you are close to 14, tell us the group size and we will plan the seating with you or suggest a second vehicle for the family.",
  },
  {
    group: THE_DAY,
    q: "Will a full quinceañera ball gown fit in the Sprinter?",
    a: "Yes. The tall cabin and wide side door let her step in standing rather than fold into a low seat. The photos here show a full ball gown at our Sprinter's door.",
  },
  {
    group: THE_DAY,
    q: "Can the Sprinter take us to the church, the photos and the salón?",
    a: "Yes. One Sprinter and chauffeur stay for your hourly booking: home, misa, photos and reception. Text me the addresses and times so we can plan the block.",
  },
  {
    group: THE_DAY,
    q: "Can our photographer take pictures with the Sprinter?",
    a: "Of course. Tell me where and when, and we'll include the time in your booking. The sunset photos here show one family's stop with our Sprinter.",
  },
];

export const Route = createFileRoute("/quinceanera-limo-atlanta")({
  head: () => ({
    meta: [
      { title: PAGE_TITLE },
      { name: "description", content: PAGE_DESC },
      {
        name: "keywords",
        content:
          "quinceanera limo atlanta, atlanta quinceanera limo service, quince limo atlanta, quinceañera transportation atlanta, sprinter limo quinceanera, limo para quinceañera atlanta",
      },
      { property: "og:title", content: PAGE_TITLE },
      { property: "og:description", content: PAGE_DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: PAGE_URL },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: PAGE_TITLE },
      { name: "twitter:description", content: PAGE_DESC },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          buildLocalBusinessSchema({
            url: PAGE_URL,
            name: "Atlanta Luxury Sprinters, Quinceañera Limo Atlanta",
            image: "https://atlantaluxurysprinters.com/favicon.png",
            description: PAGE_DESC,
            serviceType: "Quinceañera Limousine Service",
            areaServed: [
              { type: "City", name: "Atlanta" },
              { type: "City", name: "Norcross" },
              { type: "City", name: "Lawrenceville" },
              { type: "City", name: "Marietta" },
            ],
          }),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <SeoLandingPage
      eyebrow="Quinceañeras • Metro Atlanta"
      h1="Quinceañera Limo Service in Atlanta"
      intro="You've put so much into her day. I'll help you plan the Mercedes-Benz Sprinter Limousine for her court and family, from getting ready to the misa, photos and salón."
      heroImage={heroImage}
      heroPosition="50% 62%"
      heroAlt="Quinceañera in a navy ball gown beside a black Mercedes-Benz Sprinter Limousine at sunset in Atlanta"
      rich
      quickFacts={[
        { value: "Up to 14", label: "The court rides together" },
        { value: "Licensed", label: "Registered in Georgia, commercially insured" },
        { value: "Fingerprinted", label: "Background-checked chauffeurs" },
        { value: "Latina-owned", label: "Small and family-run" },
      ]}
      introImage={introImage}
      introImageAlt="Black Mercedes-Benz Sprinter Limousine at a hotel entrance in Atlanta"
      bookingBands={[
        { title: "Check Her Quinceañera Date", body: "Send us the date, the church and salón, and the size of the court. We reply with a written quote." },
        { title: "Reserve the Sprinter for Her Day", body: "Sprinters start from $250/hour. A 50% deposit secures your date." },
      ]}
      faqImage={faqImage}
      faqImageAlt="Sprinter Limousine wraparound leather seating with yellow LED ceiling lighting"
      midImage={midImage}
      midAlt="Quinceañera in a navy ball gown stepping out of the open door of a Mercedes-Benz Sprinter Limousine"
      sectionOneTitle="Room for Her Gown and Her Court"
      sectionOneBody={[
        "I know what you're trusting us with. I'll plan room for her gown, her court and the family, without rushing between stops.",
        "One chauffeur stays with your hourly booking, from home to the misa, photos and reception. We own every vehicle in our fleet.",
      ]}
      bulletsTitle="What Your Quinceañera Booking Includes"
      bulletsIntro="Every quinceañera is booked by the hour, with one Sprinter and one chauffeur who stay with you for the whole schedule."
      bullets={[
        "Sprinter Limousine seating for up to 14, so she and her court ride together",
        "Tall cabin and wide side door, made for a full ball gown",
        "LED ceiling lighting you can set to match her colors",
        "One chauffeur for the whole booking, from home to the salón",
        "Built-in cooler for water and soft drinks",
        "No alcohol for the court or any guest under 21; BYOB is for adults 21+ only",
        "Track your chauffeur live in our app, so you always know when they'll pull up",
      ]}
      coverageTitle="Quinceañeras Across Metro Atlanta"
      coverageIntro="Home pickups, the church, the photo stop and the salón, anywhere in metro Atlanta. If your venue is farther out, ask and we will quote it."
      coverage={[
        "Atlanta",
        "Norcross & Duluth",
        "Lawrenceville & Gwinnett",
        "Doraville & Chamblee",
        "Marietta & Smyrna",
        "Kennesaw & Cobb County",
        "Decatur & DeKalb",
        "College Park & South Fulton",
        "Alpharetta & Roswell",
      ]}
      detailSections={[
        {
          title: "Safety First, for Her and Her Court",
          body: [
            "Her court is mostly teenagers, and you are trusting us with all of them. This is what stands behind every quinceañera we drive:",
          ],
          list: [...SAFETY_FACTS, "No alcohol for the quinceañera, her court or any guest under 21."],
        },
        {
          title: "Comfort for the Gown and the Long Day",
          body: [
            "A quinceañera runs from the morning getting ready to the last dance, so the ride has to be comfortable the whole way.",
          ],
          list: [
            "Tall cabin and wide side door: she steps in standing up and the gown comes with her.",
            "Wraparound leather seating, so the court rides together face to face.",
            "Built-in cooler for water and soft drinks between the misa, the photos and the salón.",
            "LED ceiling lighting you can set to match her colors.",
          ],
        },
        {
          title: "Reliable From the First Pickup to the Last Drop-Off",
          list: [
            "We own every vehicle in our fleet, so the Sprinter you book is the Sprinter that arrives.",
            "One chauffeur stays with you for the whole booking, from home to the salón.",
            "Track your chauffeur live in our app, so you always know when they'll pull up.",
            "Your final quote is always in writing before you book, with no surprises on the day.",
          ],
        },
        {
          title: "Pay for It Done Right",
          body: [
            WHY_NOT_CHEAPEST,
            "Before you book any company, check these:",
          ],
          list: BEFORE_YOU_BOOK,
        },
        {
          title: "How a Quinceañera Day Usually Runs",
          body: [
            "Every family plans it a little differently. Send us your schedule and we build the booking around it. This is the order most families follow:",
          ],
          list: [
            "Pickup at home or the getting-ready location, with time to settle the gown before anyone sits down.",
            "The misa at your church, with the quinceañera and her court arriving together.",
            "A photo stop at a park, garden or downtown spot, with the Sprinter in the pictures if you like.",
            "The grand entrance at the salón.",
            "The ride home at the end of the night, with drop-offs for the court if you need them.",
          ],
          image: lounge,
          imageAlt: "Sprinter Limousine lounge with wraparound leather seating and blue LED lighting",
        },
        {
          title: "Which Vehicle Fits Your Court?",
          list: [
            "Quinceañera and a full court of damas and chambelanes, up to 14: Sprinter Limousine (Limo Style).",
            "Group of up to 13 who prefer individual seats: Executive Sprinter.",
            "Quinceañera with parents or padrinos, up to 7: Jet Sprinter.",
            "Parents and grandparents: Suburban SUV, up to 6 with captain seats or 7 with a bench.",
            "A bigger court or a big family: two vehicles, the court in the Sprinter and the family in a second vehicle.",
          ],
        },
        {
          title: "Pricing",
          body: [
            "Sprinters start from $250 per hour. Every quinceañera gets a custom quote, given upon request, based on the hours, the stops and the vehicle. A 50% deposit secures your date, and your final quote is always in writing before you book.",
          ],
        },
      ]}
      gallery={{
        title: "A Real Quinceañera With Our Sprinter",
        intro: "Our Mercedes-Benz Sprinter Limousine on the day, and the Sprinter Limousine cabin where the court rides.",
        tall: true,
        images: [
          { src: galleryA, alt: "Quinceañera holding a blue rose bouquet beside a Mercedes-Benz Sprinter Limousine at sunset", caption: "Sunset photos with the Sprinter" },
          { src: galleryB, alt: "Quinceañera ball gown at the open side door of a Mercedes-Benz Sprinter Limousine", caption: "Room for the whole gown at the door" },
          { src: limoParty, alt: "Sprinter Limousine cabin with purple LED ceiling and wraparound seating", caption: "LED lighting to match her colors" },
          { src: entry, alt: "Sprinter Limousine entry with red floor lighting", caption: "A lit entry for the grand arrival" },
          { src: greenLounge, alt: "Sprinter Limousine lounge with green LED ceiling", caption: "Wraparound seating for the court" },
        ],
      }}
      faqs={faqs}
      faqTitle="Quinceañera Limo FAQs"
      relatedTitle="Related Occasion Services"
      related={[
        { to: "/prom-limo-atlanta", title: "Prom Limo Atlanta", desc: "A Sprinter for the whole prom group, with no alcohol on board.", image: promCard, imageWhole: true },
        { to: "/wedding-transportation-atlanta", title: "Wedding Transportation", desc: "Bridal party transfers, the couple's ride and guest loops.", image: weddingCard, imageWhole: true },
        { to: "/our-story", title: "Our Story", desc: "A small, family-run, Latina-owned company in Atlanta.", image: storyCard, imageWhole: true },
      ]}
      ctaTitle="Text Me Her Quinceañera Date"
      ctaBody="Tell me the date, church, salón and court size. I'll help you choose the vehicle and send your quote in writing."
    />
  );
}
